// src/app/components/mobileStore/MobileStore.js
//
// Section « application mobile » de la page d'accueil (.app-section de
// « 01 - home v5 »). Propre à l'accueil : elle reste dans le portal.
import React from "react";
import cls from "classnames";
import { useTranslation } from "react-i18next";
import { Icon } from "@zorni/components-lib";
import styles from "./MobileStore.module.css";

/* Liens des stores : null tant que l'application n'est pas publiée. Le bouton
   s'affiche alors en « Bientôt disponible », sans lien. */
const STORE_URLS = {
  appStore: null,
  googlePlay: null,
};

/* Chiffres de l'application (note, téléchargements…). Vide tant qu'on n'a
   pas de chiffres réels : la bande est alors masquée. Format :
   { value: "4,9", label: "Note App Store" } */
const APP_STATS = [];

const PHONE_TRIPS = [
  {
    city: "Marrakech",
    meta: "6 j · Riad inclus",
    price: "dès 899 €",
    img: "https://res.cloudinary.com/hello-tickets/image/upload/c_limit,f_auto,q_auto,w_300/v1645588616/j4ssef2gssrjvzxbbqpr.jpg",
  },
  {
    city: "Fès",
    meta: "4 j · Médina",
    price: "dès 590 €",
    img: "https://cdn.getyourguide.com/img/tour/5824f8b50d3f3.jpeg/145.jpg",
  },
  {
    city: "Tanger",
    meta: "5 j · Vol inclus",
    price: "dès 720 €",
    img: "https://images.unsplash.com/photo-1533501747004-381b96042e88?auto=format&fit=crop&q=60&w=200",
  },
];

const AppleLogo = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
  </svg>
);

const PlayLogo = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 0 1-.61-.92V2.734a1 1 0 0 1 .609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.199l2.302 2.302a1 1 0 0 1 0 1.38l-2.302 2.302L15.396 13l2.302-2.492zM5.864 2.658L16.8 8.99l-2.302 2.302L5.864 2.658z" />
  </svg>
);

/* Pas d'équivalent dans la lib : barres de réseau (mode hors-ligne) et
   accueil de la barre de navigation du téléphone. */
const SignalIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
    <path d="M2 20h.01M7 20v-4M12 20v-8M17 20V8M22 4v16" />
  </svg>
);

const HomeIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M3 10.5 12 3l9 7.5V20a1 1 0 0 1-1 1h-5v-6H9v6H4a1 1 0 0 1-1-1z" />
  </svg>
);

function StoreButton({ url, logo, store, availableLabel, soonLabel, t }) {
  const content = (
    <>
      {logo}
      <span className={styles.storeText}>
        <small>{url ? availableLabel : t("portal.home.app.soonOn", "Bientôt sur")}</small>
        <strong>{store}</strong>
      </span>
    </>
  );

  if (url) {
    return (
      <a className={styles.store} href={url} target="_blank" rel="noopener noreferrer">
        {content}
      </a>
    );
  }

  return (
    <span className={cls(styles.store, styles.storeSoon)} aria-label={`${store} — ${soonLabel}`}>
      {content}
    </span>
  );
}

export function MobileStore() {
  const { t } = useTranslation();
  const soon = t("portal.home.app.soon", "Bientôt disponible");

  const features = [
    { icon: <Icon name="iconBell" size="md" color="inherit" />, tone: styles.toneAccent, title: t("portal.home.app.f1Title", "Offres privées"), text: t("portal.home.app.f1Text", "Ventes flash en avant-première") },
    { icon: <Icon name="iconFile" size="md" color="inherit" />, tone: styles.toneCoral, title: t("portal.home.app.f2Title", "Carnet de voyage"), text: t("portal.home.app.f2Text", "Tous vos documents centralisés") },
    { icon: <Icon name="iconPhone" size="md" color="inherit" />, tone: styles.toneInk, title: t("portal.home.app.f3Title", "Assistance 24/7"), text: t("portal.home.app.f3Text", "Un conseiller dédié partout") },
    { icon: <SignalIcon />, tone: styles.toneEmerald, title: t("portal.home.app.f4Title", "Mode hors-ligne"), text: t("portal.home.app.f4Text", "Vos essentiels sans connexion") },
  ];

  return (
    <section className={styles.section} aria-labelledby="app-title">
      <div className={styles.container}>
        <div className={styles.deco} aria-hidden="true">
          <div className={styles.glow} />
          <div className={styles.grid} />
        </div>

        <div className={styles.inner}>
          <header className={styles.header}>
            <p className={styles.label}>
              <span className={styles.labelDot} aria-hidden="true" />
              {t("portal.home.app.label", "Application mobile")}
            </p>
            <h2 id="app-title" className={styles.title}>
              {t("portal.home.app.titleStart", "Le monde,")}{" "}
              <em>{t("portal.home.app.titleHighlight", "au creux")}</em>{" "}
              {t("portal.home.app.titleEnd", "de votre main.")}
            </h2>
            <p className={styles.lead}>
              {t(
                "portal.home.app.lead",
                "Retrouvez l'univers Mondoviva sur votre smartphone. Une application pensée pour les voyageurs exigeants, bientôt disponible gratuitement sur iOS et Android."
              )}
            </p>
          </header>

          <div className={styles.stage}>
            {features.map((f, i) => (
              <div key={f.title} className={cls(styles.orbital, styles[`orbital${i + 1}`])}>
                <span className={cls(styles.orbitalIcon, f.tone)}>{f.icon}</span>
                <span className={styles.orbitalText}>
                  <strong>{f.title}</strong>
                  <span>{f.text}</span>
                </span>
              </div>
            ))}

            {/* Maquette d'écran, purement décorative. */}
            <div className={styles.phone} aria-hidden="true">
              <div className={styles.phoneFrame}>
                <div className={styles.screen}>
                  <div className={styles.status}>
                    <span>9:41</span>
                    <span>●●●●</span>
                  </div>
                  <div className={styles.greeting}>
                    <small>{t("portal.home.app.phoneHello", "Bonjour")}</small>
                    <strong>{t("portal.home.app.phoneTitle", "Explorer le Maroc")}</strong>
                  </div>
                  <div className={styles.search}>
                    <Icon name="iconSearch" size="sm" color="inherit" />
                    <span>{t("portal.home.app.phoneSearch", "Où partir cette année ?")}</span>
                  </div>
                  <div className={styles.sectionLabel}>{t("portal.home.app.phoneTrends", "Tendances")}</div>
                  <div className={styles.cards}>
                    {PHONE_TRIPS.map((trip) => (
                      <div key={trip.city} className={styles.card}>
                        <div className={styles.cardImg} style={{ backgroundImage: `url("${trip.img}")` }} />
                        <div className={styles.cardInfo}>
                          <strong>{trip.city}</strong>
                          <span>{trip.meta}</span>
                          <span className={styles.cardPrice}>{trip.price}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className={styles.nav}>
                    <span className={cls(styles.navItem, styles.navActive)}><HomeIcon />Accueil</span>
                    <span className={styles.navItem}><Icon name="iconSearch" size="sm" color="inherit" />Chercher</span>
                    <span className={styles.navItem}><Icon name="iconFavoriteRegular" size="sm" color="inherit" />Favoris</span>
                    <span className={styles.navItem}><Icon name="iconUser" size="sm" color="inherit" />Profil</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className={styles.stores}>
            <StoreButton
              url={STORE_URLS.appStore}
              logo={<AppleLogo />}
              store="App Store"
              availableLabel={t("portal.home.app.downloadOn", "Télécharger sur")}
              soonLabel={soon}
              t={t}
            />
            <StoreButton
              url={STORE_URLS.googlePlay}
              logo={<PlayLogo />}
              store="Google Play"
              availableLabel={t("portal.home.app.availableOn", "Disponible sur")}
              soonLabel={soon}
              t={t}
            />
          </div>

          {APP_STATS.length > 0 && (
            <dl className={styles.stats}>
              {APP_STATS.map((s) => (
                <div key={s.label} className={styles.stat}>
                  <dt className={styles.statLabel}>{s.label}</dt>
                  <dd className={styles.statValue}>{s.value}</dd>
                </div>
              ))}
            </dl>
          )}
        </div>
      </div>
    </section>
  );
}

export default MobileStore;
